import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { getDatabase, ref, push, get, set } from "firebase/database";
import LogoLetras from "../../assets/coordi_ph_logo_letras_2.png";
import AnimacionCarga from "./animacion_de_carga";
import BarraSuperior from "./barra_superior";
import BotonSOS from "./sos/boton_sos";
import { obtenerUltimasNoticias, obtenerDatosConjunto } from "../../functions/consultas";
import "../css/inicio_2.css";
import "bootstrap-icons/font/bootstrap-icons.css";


const R = {
  SUPER:         "SUPER",
  ADMINISTRADOR: "ADMINISTRADOR",
  GUARDA:        "GUARDA",
  RESIDENTE:     "RESIDENTE",
  EMPLEADO:      "EMPLEADO",
  AUDITOR:       "AUDITOR",
  CONTADOR:      "CONTADOR",
};

const NIT_TICKETS = "900362633";

const C = {
  verde:    "#66BB6A",
  cyan:     "#26C6DA",
  naranja:  "#FF3D00",
  cafe:     "#5D4037",
  morado:   "#673AB7",
  rosa:     "#EC407A",
  azul:     "#1565C0",
  rojo:     "#C62828",
  gris:     "#9E9E9E",
};

const EXT_VIDEO = [".mp4", ".webm", ".ogg", ".mov", ".avi", ".mkv", ".m4v"];
const LOCALE_CO = { year: "numeric", month: "long", day: "numeric" };

const msgModuloDeshabilitado = (m) =>
  `No se encuentra habilitado el módulo de ${m} para tu copropiedad.`;
const msgAccesoRechazado = (m) =>
  `Acceso rechazado para el módulo de ${m} de tu copropiedad.`;
const MSG_PERMISO_NO_RECONOCIDO = "Permiso no reconocido.";


const urlBase = (url = "") => url.split("?")[0].toLowerCase();

const detectarTipo = (url = "") => {
  if (!url) return "imagen";
  const u = urlBase(url);
  if (u.endsWith(".gif")) return "gif";
  return EXT_VIDEO.some((e) => u.endsWith(e)) ? "video" : "imagen";
};

const mimeVideo = (url = "") => {
  const u = urlBase(url);
  if (u.endsWith(".webm")) return "video/webm";
  if (u.endsWith(".ogg"))  return "video/ogg";
  if (u.endsWith(".mov"))  return "video/quicktime";
  return "video/mp4";
};

const formatFecha = (fecha) =>
  fecha ? new Date(fecha).toLocaleDateString("es-CO", LOCALE_CO) : "";

const recortar = (texto = "", largo = 100) =>
  texto.length > largo ? `${texto.substring(0, largo)}...` : texto;

const notificacionFlotante = (mensaje) =>
  Swal.fire({
    toast: true,
    position: "top-end",
    icon: "info",
    title: mensaje,
    showConfirmButton: false,
    timer: 3500,
    timerProgressBar: true,
  });

const registrarVisualizacion = async (noticiaId, { nit, residencia, correo }) => {
  if (!noticiaId || !nit || !residencia) return;
  try {
    const db  = getDatabase();
    const r   = ref(db, `noticias/visualizacion/${nit}/${noticiaId}/vistas`);
    const snap = await get(r);

    const vistos = [];
    if (snap.exists()) {
      Object.values(snap.val() || {}).forEach((entrada) => {
        Object.keys(entrada || {}).forEach((k) => vistos.push(k));
      });
    }
    if (vistos.includes(residencia)) return;

    await push(r, {
      [residencia]: { fecha: new Date().toISOString().substring(0, 16), correo },
    });
  } catch (error) {
    console.error("Error al registrar visualización:", error.message);
  }
};


const COLOR_ESTRELLA = "#FFB800";

const htmlCalificacion = () => `
  <div style="display:flex;flex-direction:column;align-items:center;padding:8px;">
    <img src="${LogoLetras}" alt="CoordiPH" style="height:72px;object-fit:contain;margin-top:8px;">
    <p style="font-size:22px;font-weight:600;margin:20px 0 12px;">¿Recomendarías esta App?</p>
    <p id="nps_valor" style="font-size:14px;color:#9ca3af;margin:0 0 12px;">Selecciona tu calificación:</p>
    <p style="font-size:14px;color:#9ca3af;text-align:center;margin:0 0 24px;">
      De 0 a 10, donde 0 es "nada probable" y 10 es "muy probable".
    </p>
    <div id="nps_estrellas" style="display:flex;justify-content:center;overflow-x:auto;">
      ${Array.from({ length: 10 }, (_, i) => `
        <span class="nps_estrella" data-valor="${i + 1}"
          style="cursor:pointer;padding:0 1px;font-size:26px;color:#e5e7eb;line-height:1;">★</span>`).join("")}
    </div>
  </div>`;

const mostrarCalificacionApp = async () => {
  try {
    const correoSanitizado = (localStorage.getItem("CorreoElectronico") || "")
      .replace(/[.@#$[\]]/g, "");
    if (!correoSanitizado) return;

    const db = getDatabase();
    const rutaCalificacion = ref(db, `calificacion/${correoSanitizado}`);

    const existente = await get(rutaCalificacion);
    if (existente.exists()) return;

    let rating = 0;

    const { value } = await Swal.fire({
      html: htmlCalificacion(),
      showCancelButton: true,
      confirmButtonText: "Enviar",
      cancelButtonText: "Ahora no",
      width: 460,
      didOpen: () => {
        const confirmar = Swal.getConfirmButton();
        const valor     = document.getElementById("nps_valor");
        const estrellas = Array.from(document.querySelectorAll(".nps_estrella"));

        confirmar.disabled = true;
        estrellas.forEach((estrella) => {
          estrella.addEventListener("click", () => {
            rating = Number(estrella.dataset.valor);
            estrellas.forEach((e, i) => {
              e.style.color = i < rating ? COLOR_ESTRELLA : "#e5e7eb";
            });
            valor.textContent = `${rating}/10`;
            valor.style.color = COLOR_ESTRELLA;
            confirmar.disabled = false;
          });
        });
      },
      preConfirm: () => rating,
    });

    if (!value) return;
    await set(rutaCalificacion, value);
  } catch (error) {
    console.error("Error en la calificación:", error.message);
  }
};


const construirModulos = ({ permisos, modulos, esClienteEspecial, ir, aviso }) => {
  const esGuardaOEmpleado    = permisos === R.GUARDA    || permisos === R.EMPLEADO;
  const esResidenteOEmpleado = permisos === R.RESIDENTE || permisos === R.EMPLEADO;
  const esEmpleado           = permisos === R.EMPLEADO;

  const activo = (valor) => valor !== false;

  const colorOGris = (habilitado, color) => (habilitado ? color : C.gris);

  const puedeAcceder = (nombre, habilitado, bloquearEmpleado = true) => {
    if (!habilitado)                      { aviso(msgModuloDeshabilitado(nombre)); return false; }
    if (bloquearEmpleado && esEmpleado)   { aviso(msgAccesoRechazado(nombre));     return false; }
    return true;
  };

  const navegarPorPermiso = (rutasPorRol, mensaje) => {
    const ruta = rutasPorRol[permisos];
    if (ruta) ir(ruta);
    else aviso(mensaje || MSG_PERMISO_NO_RECONOCIDO);
  };

  return [
    {
      clave: "mi_propiedad",
      color: C.verde,
      icono: "bi-house-door-fill",
      etiqueta: "Mi propiedad",
      onClick: () => ir("/mi_propiedad"),
    },
    {
      clave: "visitantes",
      color: colorOGris(!esEmpleado && activo(modulos.moduloVisitantes), C.cyan),
      icono: "bi-person-badge-fill",
      etiqueta: "Visitantes",
      onClick: () => {
        if (!puedeAcceder("visitantes", activo(modulos.moduloVisitantes))) return;
        navegarPorPermiso({
          [R.RESIDENTE]:     "/visitantes",
          [R.ADMINISTRADOR]: "/visitantes",
          [R.SUPER]:         "/visitantes",
          [R.GUARDA]:        "/visitantes",
        });
      },
    },
    {
      clave: "reservas",
      color: colorOGris(activo(modulos.moduloReservas), C.naranja),
      icono: "bi-dribbble",
      etiqueta: esGuardaOEmpleado ? "Ver reservas" : "Reservar",
      onClick: () => {
        if (!activo(modulos.moduloReservas)) { aviso(msgModuloDeshabilitado("reservas")); return; }
        // Igual que la app: todos entran a "Elige la zona social"; solo cambia el texto del botón.
        ir("/crear_reserva");
      },
    },
    {
      clave: "encomiendas",
      color: colorOGris(!esEmpleado && activo(modulos.moduloEncomiendas), C.cafe),
      icono: "bi-box-seam-fill",
      etiqueta: "Encomiendas",
      onClick: () => {
        if (!puedeAcceder("encomiendas", activo(modulos.moduloEncomiendas))) return;
        navegarPorPermiso({
          [R.RESIDENTE]:     "/encomiendas",
          [R.GUARDA]:        "/encomiendas",
          [R.ADMINISTRADOR]: "/encomiendas",
          [R.SUPER]:         "/encomiendas",
        });
      },
    },
    {
      clave: "pagos",
      color: colorOGris(!esEmpleado && activo(modulos.moduloPagos), C.morado),
      icono: permisos === R.GUARDA ? "bi-bell-fill" : "bi-currency-dollar",
      etiqueta: permisos === R.GUARDA ? "Notificaciones" : "Pagos",
      onClick: () => {
        if (!activo(modulos.moduloPagos)) { aviso(msgModuloDeshabilitado("pagos")); return; }
        if (esEmpleado)           { aviso(msgAccesoRechazado("pagos"));     return; }
        if (permisos === R.GUARDA) { ir("/notificaciones"); return; }
        if (modulos.urlPagos) {
          window.open(`https://${modulos.urlPagos}`, "_blank", "noreferrer");
          return;
        }
        ir(permisos === R.RESIDENTE ? "/mi_cartera" : "/cartera");
      },
    },
    {
      clave: "pqrs",
      color: colorOGris(!esEmpleado && activo(modulos.moduloPqrs), C.rosa),
      icono: permisos === R.GUARDA ? "bi-people-fill" : "bi-chat-left-text-fill",
      etiqueta: permisos === R.GUARDA ? "Usuarios" : "PQR",
      onClick: () => {
        if (!puedeAcceder("Pqrs", activo(modulos.moduloPqrs))) return;
        if (permisos === R.GUARDA) { ir("/usuarios"); return; }
        navegarPorPermiso({
          [R.SUPER]:         "/pqrs",
          [R.ADMINISTRADOR]: "/pqrs",
          [R.RESIDENTE]:     "/pqrs",
        });
      },
    },
    {
      clave: "anuncios",
      color: colorOGris(activo(modulos.moduloAnuncios), C.azul),
      icono: esGuardaOEmpleado
        ? "bi-exclamation-triangle-fill"
        : esClienteEspecial
          ? "bi-headset"
          : "bi-bag-fill",
      etiqueta: esGuardaOEmpleado
        ? "Novedades"
        : esClienteEspecial
          ? "Tickets"
          : "Anuncios",
      onClick: () => {
        if (!activo(modulos.moduloAnuncios)) { aviso(msgModuloDeshabilitado("anuncios")); return; }
        switch (permisos) {
          case R.SUPER:
          case R.ADMINISTRADOR:
          case R.RESIDENTE:
            ir(esClienteEspecial ? "/tickets" : "/anuncios");
            break;
          case R.GUARDA:
          case R.EMPLEADO:
            ir("/novedades");
            break;
          default:
            aviso(MSG_PERMISO_NO_RECONOCIDO);
        }
      },
    },
    {
      clave: "ultimo",
      color: C.rojo,
      icono: permisos === R.GUARDA
        ? "bi-qr-code-scan"
        : esResidenteOEmpleado
          ? "bi-bell-fill"
          : "bi-shield-fill-check",
      etiqueta: permisos === R.GUARDA
        ? "Rondas"
        : esResidenteOEmpleado
          ? "Notificaciones"
          : "Usuarios",
      onClick: () => {
        switch (permisos) {
          case R.GUARDA:
            ir("/rondas");
            break;
          case R.RESIDENTE:
          case R.EMPLEADO:
            ir("/notificaciones");
            break;
          case R.SUPER:
          case R.ADMINISTRADOR:
            ir("/usuarios");
            break;
          default:
            aviso(MSG_PERMISO_NO_RECONOCIDO);
        }
      },
    },
  ];
};


const BotonModulo = ({ modulo }) => (
  <button className="inicio2Modulo" onClick={modulo.onClick} title={modulo.etiqueta}>
    <span className="inicio2ModuloBoton" style={{ backgroundColor: modulo.color }}>
      <i className={`bi ${modulo.icono}`} />
    </span>
    <span className="inicio2ModuloEtiqueta">{modulo.etiqueta}</span>
  </button>
);

const MediaNoticia = ({ noticia }) => {
  if (!noticia.url_foto1) {
    return <div className="inicio2NoticiaSinMedia">📰</div>;
  }
  if (noticia.tipo === "video") {
    return (
      <>
        <video preload="metadata" muted>
          <source src={noticia.url_foto1} type={mimeVideo(noticia.url_foto1)} />
        </video>
        <span className="inicio2NoticiaBadge"><i className="bi bi-camera-video-fill" /> VIDEO</span>
      </>
    );
  }
  return (
    <>
      <img src={noticia.url_foto1} alt={noticia.titulo} />
      {noticia.tipo === "gif" && (
        <span className="inicio2NoticiaBadge"><i className="bi bi-filetype-gif" /> GIF</span>
      )}
    </>
  );
};

const TarjetaNoticia = ({ noticia, onAbrir, onVisible }) => {
  const refCallback = useCallback((nodo) => {
    if (!nodo) return;
    const observer = new IntersectionObserver(
      (entradas) => {
        entradas.forEach((e) => {
          if (e.intersectionRatio > 0.5) {
            onVisible(noticia.id);
            observer.disconnect();
          }
        });
      },
      { threshold: [0.5] }
    );
    observer.observe(nodo);
  }, [noticia.id, onVisible]);

  return (
    <article className="inicio2Noticia" ref={refCallback} onClick={() => onAbrir(noticia)}>
      <div className="inicio2NoticiaTitulo">{noticia.titulo}</div>
      <div className="inicio2NoticiaMedia"><MediaNoticia noticia={noticia} /></div>
      {noticia.descripcion && (
        <p className="inicio2NoticiaDescripcion">{recortar(noticia.descripcion)}</p>
      )}
    </article>
  );
};

const ModalNoticia = ({ noticia, onCerrar }) => (
  <div className="inicio2ModalFondo" onClick={onCerrar}>
    <div className="inicio2Modal" onClick={(e) => e.stopPropagation()}>
      <div className="inicio2ModalCabecera">
        <span>{noticia.titulo}</span>
        <button className="inicio2ModalCerrar" onClick={onCerrar} aria-label="Cerrar">×</button>
      </div>

      {noticia.url_foto1 && (noticia.tipo === "video" ? (
        <video controls autoPlay playsInline preload="auto">
          <source src={noticia.url_foto1} type={mimeVideo(noticia.url_foto1)} />
        </video>
      ) : (
        <img src={noticia.url_foto1} alt={noticia.titulo} />
      ))}

      <div className="inicio2ModalCuerpo">
        <p style={{ margin: 0 }}>{noticia.descripcion}</p>
        {noticia.url_pdf && (
          <a className="inicio2ModalEnlace" href={noticia.url_pdf} target="_blank" rel="noreferrer">
            📄 Ver documento adjunto
          </a>
        )}
        {noticia.enlace_externo && (
          <a className="inicio2ModalEnlace" href={noticia.enlace_externo} target="_blank" rel="noreferrer">
            🔗 Abrir enlace
          </a>
        )}
        <span className="inicio2ModalFecha">{formatFecha(noticia.fecha)}</span>
      </div>
    </div>
  </div>
);


const Inicio2 = memo(() => {
  const navigate = useNavigate();

  const [noticias,     setNoticias]     = useState([]);
  const [conjunto,     setConjunto]     = useState({});
  const [cargando,     setCargando]     = useState(true);
  const [modalNoticia, setModalNoticia] = useState(null);
  const vistas = useRef(new Set());

  const usuario = useMemo(() => ({
    nombres:   localStorage.getItem("NombreCompleto")    || "",
    permisos:  localStorage.getItem("Permisos")          || "",
    nit:       localStorage.getItem("CodigoCliente")     || "",
    razon:     localStorage.getItem("RazonSocial")       || "",
    direccion: localStorage.getItem("Direccion")         || "",
    correo:    localStorage.getItem("CorreoElectronico") || "",
  }), []);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [datosNoticias, datosConjunto] = await Promise.all([
        obtenerUltimasNoticias(),
        obtenerDatosConjunto(),
      ]);

      const lista = (datosNoticias || []).map((item) => ({
        ...item,
        fecha: item.fecha ? item.fecha.split(" ")[0] : "",
        tipo:  detectarTipo(item.url_foto1 || ""),
      }));

      setNoticias(lista);
      setConjunto(datosConjunto || {});
    } catch (error) {
      console.error("Error al cargar el inicio:", error.message);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const temporizador = setTimeout(mostrarCalificacionApp, 1000);
    return () => clearTimeout(temporizador);
  }, []);

  useEffect(() => {
    if (cargando) return;
    if (conjunto.moduloPrincipal === "SOS")      navigate("/panico",    { replace: true });
    if (conjunto.moduloPrincipal === "INMUEBLE") navigate("/inmuebles", { replace: true });
  }, [cargando, conjunto.moduloPrincipal, navigate]);

  const marcarVista = useCallback((id) => {
    if (vistas.current.has(id)) return;
    vistas.current.add(id);
    registrarVisualizacion(id, {
      nit: usuario.nit,
      residencia: usuario.direccion,
      correo: usuario.correo,
    });
  }, [usuario.nit, usuario.direccion, usuario.correo]);

  const abrirNoticia = useCallback((noticia) => {
    if (!noticia.url_foto1 && noticia.enlace_externo) {
      window.open(noticia.enlace_externo, "_blank", "noreferrer");
      return;
    }
    setModalNoticia(noticia);
  }, []);

  const esClienteEspecial = usuario.nit === NIT_TICKETS;

  const modulos = useMemo(() => construirModulos({
    permisos: usuario.permisos,
    modulos: conjunto,
    esClienteEspecial,
    ir: (ruta) => navigate(ruta),
    aviso: notificacionFlotante,
  }), [usuario.permisos, conjunto, esClienteEspecial, navigate]);

  if (cargando) return <AnimacionCarga />;

  return (
    <>
      <BarraSuperior />

      {modalNoticia && (
        <ModalNoticia noticia={modalNoticia} onCerrar={() => setModalNoticia(null)} />
      )}

      <div className="inicio2Pagina">
        <header className="inicio2Encabezado">
          <h1 className="inicio2Saludo">¿Cómo te podemos ayudar hoy?</h1>
        </header>

        <nav className="inicio2Grid" aria-label="Accesos rápidos">
          {modulos.map((modulo) => (
            <BotonModulo key={modulo.clave} modulo={modulo} />
          ))}
        </nav>

        <section className="inicio2Noticias">
          <div className="inicio2NoticiasCabecera">
            <h2 className="inicio2SeccionTitulo">Noticias</h2>
            {noticias.length > 0 && (
              <button className="inicio2VerTodas" onClick={() => navigate("/noticias")}>
                Ver todas las noticias <i className="bi bi-arrow-right" />
              </button>
            )}
          </div>

          {noticias.length > 0 && (
            <div className="inicio2Carrusel">
              {noticias.map((noticia) => (
                <TarjetaNoticia
                  key={noticia.id}
                  noticia={noticia}
                  onAbrir={abrirNoticia}
                  onVisible={marcarVista}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <BotonSOS />
    </>
  );
});

export default Inicio2;
